import { Pipe, PipeTransform } from '@angular/core';
import { DatePipe } from '@angular/common';

/**
 * OutlookDatePipe formats dates in Outlook-style format:
 * - Same day: "Idag, 14:30"
 * - Yesterday: "Igår, 14:30" 
 * - This week (Monday-Sunday): "Måndag, 14:30"
 * - Same year: "15 mar, 14:30"
 * - Different year: "15 mar 2023, 14:30"
 */
@Pipe({
  name: 'outlookDate',
  standalone: false
})
export class OutlookDatePipe implements PipeTransform {

  constructor(private datePipe: DatePipe) {}

  transform(value: any, locale: string = 'sv'): string {
    if (!value) return '';

    const inputDate = new Date(value);
    const now = new Date();
    
    // Normalize dates to remove time component for comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const inputDateOnly = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());
    
    // Calculate time difference in days
    const diffTime = today.getTime() - inputDateOnly.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Get start of current week (Monday)
    const startOfWeek = new Date(today);
    const dayOfWeek = startOfWeek.getDay() || 7; // Convert Sunday (0) to 7
    startOfWeek.setDate(today.getDate() - dayOfWeek + 1);
    startOfWeek.setHours(0, 0, 0, 0);
    
    // Check if the date is today
    if (diffDays === 0) {
      // Today - show "Idag, HH:mm"
      const timeFormat = this.datePipe.transform(inputDate, 'HH:mm', locale) || '';
      return `Idag, ${timeFormat}`;
    }
    
    // Check if the date is yesterday  
    if (diffDays === 1) {
      // Yesterday - show "Igår, HH:mm"
      const timeFormat = this.datePipe.transform(inputDate, 'HH:mm', locale) || '';
      return `Igår, ${timeFormat}`;
    }
    
    // Check if the date is within current week (and in the past)
    if (inputDateOnly >= startOfWeek && diffDays > 0 && diffDays < 7) {
      // This week - show "Day name, HH:mm" (e.g., "måndag, 14:30")
      const dayName = this.datePipe.transform(inputDate, 'EEEE', locale) || '';
      const timeFormat = this.datePipe.transform(inputDate, 'HH:mm', locale) || '';
      // Capitalize first letter for better presentation
      const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
      return `${capitalizedDayName}, ${timeFormat}`;
    }
    
    // Check if the date is within current year
    if (inputDate.getFullYear() === now.getFullYear()) {
      // Same year - show "d MMM, HH:mm" (e.g., "15 mar, 14:30")
      return this.datePipe.transform(inputDate, 'yyyy-MM-dd', locale) || '';
    }
    
    // Different year - show full date "d MMM yyyy, HH:mm" (e.g., "15 mar 2023, 14:30")
    return this.datePipe.transform(inputDate, 'yyyy-MM-dd', locale) || '';
  }
}